import * as Headless from "@headlessui/react";
import clsx from "clsx";
import type React from "react";
import { forwardRef } from "react";

export const Select = forwardRef(function Select(
	{
		className,
		multiple,
		showArrow = true,
		...props
	}: { className?: string; showArrow?: boolean } & Omit<
		Headless.SelectProps,
		"as" | "className"
	>,
	ref: React.ForwardedRef<HTMLSelectElement>,
) {
	return (
		<span
			data-slot="control"
			className={clsx([
				className,
				// Basic layout
				"group relative block w-full",
				// Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
				"before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-base-100 before:shadow-sm",
				// Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
				"dark:before:hidden",
				// Focus ring
				"after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-transparent after:ring-inset has-data-focus:after:ring-2 has-data-focus:after:ring-primary",
				// Disabled state
				"has-data-disabled:opacity-50 has-data-disabled:before:bg-base-content/5 has-data-disabled:before:shadow-none",
			])}
		>
			<Headless.Select
				ref={ref}
				multiple={multiple}
				{...props}
				className={clsx([
					// Basic layout
					"relative block w-full appearance-none rounded-lg py-[calc(--spacing(2.5)-1px)] sm:py-[calc(--spacing(1.5)-1px)]",
					// Horizontal padding
					multiple
						? "px-[calc(--spacing(3.5)-1px)] sm:px-[calc(--spacing(3)-1px)]"
						: "pr-[calc(--spacing(10)-1px)] pl-[calc(--spacing(3.5)-1px)] sm:pr-[calc(--spacing(9)-1px)] sm:pl-[calc(--spacing(3)-1px)]",
					// Options (multi-select)
					"[&_optgroup]:font-semibold",
					// Typography
					"text-base/6 text-base-content placeholder:text-base-content/50 sm:text-sm/6 dark:*:text-base-content",
					// Border
					"border border-base-content/10 data-hover:border-base-content/20",
					// Background color
					"bg-transparent dark:bg-base-content/5 dark:*:bg-base-100",
					// Hide default focus styles
					"focus:outline-hidden",
					// Invalid state
					"data-invalid:border-error data-invalid:data-hover:border-error",
					// Disabled state
					"data-disabled:border-base-content/20 data-disabled:opacity-100 dark:data-disabled:border-base-content/15 dark:data-disabled:bg-base-content/2.5 dark:data-hover:data-disabled:border-base-content/15",
				])}
			/>
			{showArrow && !multiple && (
				<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
					<svg
						className="size-5 stroke-base-content/50 group-has-data-disabled:stroke-base-content/60 sm:size-4 forced-colors:stroke-[CanvasText]"
						viewBox="0 0 16 16"
						aria-hidden="true"
						fill="none"
					>
						<path
							d="M5.75 10.75L8 13L10.25 10.75"
							strokeWidth={1.5}
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
						<path
							d="M10.25 5.25L8 3L5.75 5.25"
							strokeWidth={1.5}
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</span>
			)}
		</span>
	);
});
